import axios from 'axios';
import { KeepaliveWebSocket } from './KeepaliveWebSocket';
import { Message, pingMessage } from './Messages';

export const notify = 'notify';

export class NotifyEvent extends Event {
	public constructor(public readonly message: Message) {
		super(notify);
	}

	public readonly type: typeof notify;
}

export type NotifyListener = (this: AsyncNotifications, event: NotifyEvent) => any;

/**
 * Encapsulates a single, centralized, resilient notification hub using the following techniques:
 *
 * - Enforcing the singleton pattern
 * - Sending periodic keepalive messages over a websocket
 * - Recreating that websocket after closure by its peer
 * - Shielding the websocket object from being tampered with by clients to achieve reliability for
 *   all clients
 *
 * Clients must listen for `'notify'` events, instead of `WebSocket`'s `'message'` events, where the
 * payload has already been parsed into `'NotifyEvent.message'`.
 */
export class AsyncNotifications {
	private constructor(
		private readonly _sessionUrl,
		private readonly _wsUrl,
		private readonly _redirectUrl,
	) {}

	public static get singleton(): AsyncNotifications {
		return (AsyncNotifications._singleton =
			AsyncNotifications._singleton ??
			new AsyncNotifications(
				`${process.env.VUE_APP_CORE_SESSIONS_URI}/sessions`,
				process.env.VUE_APP_WS_URI,
				process.env.VUE_APP_SESSIONS_REDIRECT_URL,
			));
	}

	private static _singleton: AsyncNotifications | undefined;

	public addEventListener(
		type: typeof notify,
		listener: NotifyListener,
		options?: boolean | AddEventListenerOptions,
	): void {
		this._eventTarget.addEventListener(type, listener, options);
	}

	public removeEventListener(
		type: typeof notify,
		listener: NotifyListener,
		options?: boolean | AddEventListenerOptions,
	): void {
		this._eventTarget.removeEventListener(type, listener, options);
	}

	public async connect(token: string): Promise<void> {
		if (token !== this._token) {
			this.disconnect();

			try {
				const sendConnection = await axios({
					method: 'POST',
					url: this._sessionUrl,
					headers: {
						'Content-Type': 'application/json',
						Authorization: token,
					},
					data: {
						redirectUri: this._redirectUrl,
						idToken: token,
					},
				});

				const locationUrl = new URL(sendConnection.headers.location);
				const code: string = locationUrl.searchParams.get('code');

				const websocketUrl = `${this._wsUrl}?tok=${code}`;
				this.createResilientWebSocket(websocketUrl);
			} catch (error) {
				console.error('Failed to connect', error);
				throw error;
			}
		}
	}

	public disconnect(): void {
		this._destroyResilientWebSocket?.();
	}

	private createResilientWebSocket(url: string): void {
		const keepaliveMessage: string = JSON.stringify(pingMessage());
		const keepaliveMilliseconds = 15000;
		this._ws = new KeepaliveWebSocket(url, keepaliveMessage, keepaliveMilliseconds);

		this._ws.addEventListener('open', (): void => {
			console.log('ws->open');
		});

		const closeListener = (): void => {
			console.log(`ws->close`);
			this.createResilientWebSocket(url);
		};
		this._ws.addEventListener('close', closeListener);

		this._ws.addEventListener('message', (messageEvent: MessageEvent): void => {
			console.log(`ws->message`);

			// Dispatch a `NotifyEvent` to listeners.
			const message = JSON.parse(messageEvent.data) as Message;
			const notifyEvent = new NotifyEvent(message);
			this._eventTarget.dispatchEvent(notifyEvent);
		});

		this._destroyResilientWebSocket = () => {
			this._ws.removeEventListener('close', closeListener);

			this._ws.close();
			this._ws = undefined;

			this._destroyResilientWebSocket = undefined;
		};
	}

	private _eventTarget = new EventTarget();
	private _token: string | undefined;
	private _ws: WebSocket | undefined;
	private _destroyResilientWebSocket: (() => void) | undefined;
}
